<template>
    <div>
        <!-- one tab per building plus one for everything nobody has placed yet.
             With no buildings defined there is a single tab and the page looks
             exactly as it did before. -->
        <b-nav v-if="buildingTabs.length > 1" pills class="mb-3">
            <b-nav-item
                v-for="(tab, index) in buildingTabs"
                :key="index"
                :active="index === activeBuildingTab"
                v-on:click="activeBuildingTab = index">{{tab.name}}</b-nav-item>
        </b-nav>

        <div class="animated fadeIn row">
            <div class="col-12 col-md-6 col-lg-4 mb-3">
                <b-form-input
                    v-model="filter"
                    v-bind:placeholder="$t('SEARCHMODULES')"/>
            </div>
        </div>

        <div class="animated fadeIn row">
            <div class="col-12 col-xl-6 mb-3"
                v-for="station in weatherStations"
                :key="station.moduleId">
                <weather-card :module="station"/>
            </div>
        </div>

        <p class="text-muted small mb-2" v-if="filter !== ''">{{ $t('REORDERFILTERED') }}</p>

        <div v-for="group in moduleGroups" :key="group.key" class="module-group">
            <h5 class="module-group-head" v-on:click="toggleGroup(group.key)">
                <i class="icon-arrow-down mr-2" v-if="!isFolded(group.key)"></i>
                <i class="icon-arrow-right mr-2" v-else></i>
                {{ group.name }}
                <span class="text-muted ml-2">{{ group.modules.length }}</span>
            </h5>

            <draggable
                v-show="!isFolded(group.key)"
                class="animated fadeIn row"
                :value="group.modules"
                v-on:input="(value) => reorderGroup(group, value)"
                handle=".module-drag"
                :disabled="filter !== ''"
                :animation="150">
                <div class="col-sm-6 col-lg-3" v-for="item in group.modules" :key="item.moduleId">
                <!-- An air conditioner is not a relay: it is told over infrared
                     and its state lives with the rest of the hvac, so it gets
                     power and mode rather than a switch that would do nothing. -->
                <b-card no-body class="modules" v-if="item.moduleVersion === 23">
                    <div class="line" v-bind:style="'background-color: '+getColor(item)+';'"></div>
                    <b-card-body class="pb-1 pt-1">
                        <div class="d-flex align-items-center">
                            <div class="module-icon module-icon-sm mr-2 module-drag">
                                <module-icon :module="item"/>
                            </div>
                            <h2 class="mb-0 mr-auto">{{item.moduleName}}</h2>
                            <b-button class="nsp-power nsp-power-sm ml-2"
                                v-bind:variant="powerVariant(acState.on)"
                                v-on:click.prevent="setAcPower(!acState.on)">
                                <i class="icon-power"></i>
                            </b-button>
                        </div>
                        <div class="d-flex align-items-center mt-2" v-if="acState.on">
                            <span class="nsp-big mr-auto">{{acState.temp}}<small v-if="acState.temp !== '-'">&#8451;</small></span>
                            <b-button size="sm" class="ml-1"
                                v-bind:variant="acState.mode === 'cool' ? 'info' : 'outline-secondary'"
                                v-on:click.prevent="setAcMode('cool')">{{$t('NSP_COOL')}}</b-button>
                            <b-button size="sm" class="ml-1"
                                v-bind:variant="acState.mode === 'heat' ? 'danger' : 'outline-secondary'"
                                v-on:click.prevent="setAcMode('heat')">{{$t('NSP_HEAT')}}</b-button>
                            <b-button size="sm" class="ml-1"
                                v-bind:variant="acState.mode === 'fan' ? 'primary' : 'outline-secondary'"
                                v-on:click.prevent="setAcMode('fan')">{{$t('NSP_FAN')}}</b-button>
                        </div>
                        <div class="text-muted small mt-2">
                            {{ $t('NSP_AC_SETPOINTS', { cool: acTempState.cool, heat: acTempState.heat }) }}
                        </div>
                        <div class="text-muted small" v-if="acState.lowestTemperature != null">
                            {{ $t('NSP_ROOM_TEMP_RANGE') }}:
                            {{ acState.lowestTemperature }}&#8451; - {{ acState.highestTemperature }}&#8451;
                        </div>
                    </b-card-body>
                </b-card>
                <b-card no-body class="modules" v-else-if="item.moduleVersion === 6">
                    <div class="line" v-bind:style="'background-color: '+getColor(item)+';'"></div>
                    <b-card-body class="pb-1 pt-1 pr-1">
                        <div v-if="hasTemperature(item)" class="float-left temperature">{{item.temperature}} &#8451;</div>
                    </b-card-body>
                    <b-card-body class="pb-1 pt-1">
                        <div class="d-flex align-items-center">
                            <div class="module-icon module-icon-sm mr-2 module-drag">
                                <module-icon :module="item"/>
                            </div>
                            <h2 class="mb-0 mr-auto">{{item.moduleName}}</h2>
                            <b-button class="nsp-power nsp-power-sm ml-2"
                                v-bind:variant="powerVariant(isThermostatOn(item))"
                                v-on:click.prevent="toggleThermostat(item)">
                                <i class="icon-power"></i>
                            </b-button>
                        </div>
                        <div class="row" v-if="item.values.deviceOn === 'true' ||item.values.deviceOn === true">
                            <div class="col-4" v-on:mouseup="setThermostat(item)">
                                <round-slider
                                    v-model="item.values.targetTemperature"
                                    start-angle="0"
                                    end-angle="+180"
                                    line-cap="round"
                                    min="10"
                                    max="30"
                                    radius="90"/>
                            </div>
                            <div class="col-8 mt-4">
                                <div class="row ml-4">
                                    <div class="col-8 d-flex">
                                        <div class="mr-2" v-on:click="item.values.systemMode = 'heat'; setThermostat(item)">
                                            <svg xmlns="http://www.w3.org/2000/svg" width="44" height="44" viewBox="0 0 44 44">
                                                <g fill="none" fill-rule="evenodd">
                                                    <path d="M-2 47h48V-1H-2z"/>
                                                    <path :fill="(item.values.systemMode == 'heat') ? item.iconColor : '#CCCCCC'" d="M5.1 36.08l3.58-3.6 2.82 2.82-3.58 3.6-2.82-2.82zM20 43.9V38h4v5.9h-4zM22 10c6.62 0 12 5.38 12 12s-5.38 12-12 12c-6.618 0-12-5.38-12-12s5.382-12 12-12zm16 14v-4h6v4h-6zm-5.52 11.32l2.8-2.8 3.602 3.58-2.822 2.82-3.58-3.6zm6.42-27.4l-3.58 3.58-2.82-2.82 3.58-3.58 2.82 2.82zM20 6V.1h4V6h-4zM0 24v-4h6v4H0zM11.52 8.68L8.68 11.5 5.1 7.92 7.92 5.1l3.6 3.58z"/>
                                                </g>
                                            </svg>
                                        </div>
                                        <div class="mr-2" v-on:click="item.values.systemMode = 'cool'; setThermostat(item)">
                                            <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 40 40">
                                                <g fill="none" fill-rule="evenodd">
                                                    <path d="M-4 44h48V-4H-4z"/>
                                                    <path :fill="(item.values.systemMode == 'cool') ? item.iconColor : '#CCCCCC'" d="M40 18h-8.34l6.48-6.48-2.82-2.84L26 18h-4v-4l9.32-9.32-2.84-2.82L22 8.34V0h-4v8.34l-6.48-6.48-2.84 2.82L18 14v4h-4L4.68 8.68l-2.82 2.84L8.34 18H0v4h8.34l-6.48 6.48 2.82 2.84L14 22h4v4l-9.32 9.32 2.84 2.82L18 31.66V40h4v-8.34l6.48 6.48 2.84-2.82L22 26v-4h4l9.32 9.32 2.82-2.84L31.66 22H40z"/>
                                                </g>
                                            </svg>
                                        </div>
                                        <div class="mr-2" v-on:click="item.values.systemMode = 'fan_only'; setThermostat(item)">
                                            <svg xmlns="http://www.w3.org/2000/svg" width="40" height="37" viewBox="0 0 40 37">
                                                <g fill="none" fill-rule="evenodd">
                                                    <path d="M-4 43h48V-5H-4z"/>
                                                    <path :fill="(item.values.systemMode == 'fan_only') ? item.iconColor : '#CCCCCC'" d="M20 15.23c-1.994 0-3.752.96-4.89 2.424-2.02-4.75-3.454-10.77-3.454-12.254 0-2.534 3.736-4.588 8.344-4.588 4.61 0 8.344 2.054 8.344 4.588 0 1.484-1.432 7.506-3.454 12.256-1.138-1.466-2.896-2.426-4.89-2.426zm-6.215 6.215c0 3.05 2.212 5.582 5.116 6.104-3.116 4.054-7.464 8.144-8.726 8.87-2.208 1.276-5.88-.948-8.198-4.968-2.318-4.02-2.404-8.31-.194-9.586 1.314-.758 7.462-2.584 12.678-3.18a6.137 6.137 0 0 0-.676 2.76zm24.43.473c2.208 1.282 2.108 5.574-.22 9.586-2.33 4.012-6.008 6.226-8.216 4.946-1.26-.732-5.596-4.836-8.698-8.9 2.912-.512 5.134-3.048 5.134-6.104 0-.99-.254-1.914-.668-2.746 5.214.614 11.358 2.456 12.668 3.218zm-14-.473A4.218 4.218 0 0 1 20 25.66a4.219 4.219 0 0 1-4.216-4.214A4.219 4.219 0 0 1 20 17.231a4.218 4.218 0 0 1 4.214 4.214z"/>
                                                </g>
                                            </svg>
                                        </div>
                                    </div>
                                    <div class="col-4">
                                        <label>{{$t('FANMODE')}}</label>
                                        <select v-on:change="setThermostat(item)" v-model="item.values.fanMode">
                                            <option value="auto">{{$t('AUTO')}}</option>
                                            <option value="low">{{$t('LOW')}}</option>
                                            <option value="medium">{{$t('MEDIUM')}}</option>
                                            <option value="high">{{$t('HIGH')}}</option>
                                        </select>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </b-card-body>
                </b-card>
                <b-card no-body class="modules" v-else-if="item.moduleVersion === 16">
                    <div class="line" v-bind:style="'background-color: '+getColor(item)+';'"></div>
                    <b-card-body class="pb-1 pt-1 pr-1">
                        <div v-if="hasTemperature(item)" class="float-left temperature">{{item.temperature}} &#8451;</div>
                        <div v-if="item.offline" class="mr-2 mt-1 text-right">
                            <i class="icon-exclamation" style="color: red;"></i>
                        </div>
                    </b-card-body>
                    <b-card-body class="pb-1 pt-1">
                        <div class="d-flex align-items-center">
                            <div class="module-icon module-icon-sm mr-2 module-drag">
                                <module-icon :module="item"/>
                            </div>
                            <h2 class="mb-0 mr-auto">{{item.moduleName}}</h2>
                            <b-button class="nsp-power nsp-power-sm ml-2"
                                v-bind:variant="powerVariant(isRelayOn(item))"
                                v-on:click.prevent="toggleDimmer(item)">
                                <i class="icon-power"></i>
                            </b-button>
                        </div>
                        <!-- brightness is a percentage, not a temperature, so it
                             gets the straight slider the recuperator fans use.
                             The dial is kept for temperatures alone. -->
                        <div class="px-3 pb-2" v-if="item.currentValue === 1">
                            <div class="d-flex align-items-center small text-muted">
                                <span>{{$t('BRIGHTNESS')}}</span>
                                <span class="ml-auto">{{item.values.brightness}}%</span>
                            </div>
                            <b-form-input
                                type="range"
                                v-model.number="item.values.brightness"
                                min="0"
                                max="100"
                                step="1"
                                v-on:change="setDimmer(item)"/>
                        </div>
                    </b-card-body>
                    <div v-if="item.tempShown">
                        <div class="m-3 text-right" v-on:click="setGlobalGraphData(item, 'temperature')">
                            <i class="icon-crop"></i>
                        </div>
                        <series-chart :points="temperaturePoints(item)" :label="$t('TEMPERATURE')" class="px-3" :height="140" />
                    </div>
                </b-card>
                <!-- An nspanel is a room thermostat. Only the heating is offered
                     here: lights, shutters, ventilation and the ac belong to the
                     room card on the house screen, not to a device list. -->
                <b-card no-body class="modules" v-else-if="item.moduleVersion === 13">
                    <div class="line" v-bind:style="'background-color: '+getColor(item)+';'"></div>
                    <b-card-body class="pb-1 pt-1 pr-1">
                        <div v-if="hasTemperature(item)" class="float-left temperature">{{item.temperature}} &#8451;</div>
                        <div v-if="item.offline" class="mr-2 mt-1 text-right">
                            <i class="icon-exclamation" style="color: red;"></i>
                        </div>
                    </b-card-body>
                    <b-card-body class="pb-1 pt-1">
                        <div class="d-flex align-items-center">
                            <div class="module-icon module-icon-sm mr-2 module-drag">
                                <module-icon :module="item"/>
                            </div>
                            <h2 class="mb-0 mr-auto">{{item.moduleName}}</h2>
                            <b-badge v-if="item.secRelayStatus === 1" variant="danger" class="mr-2">
                                {{$t('NSP_ACTIVE')}}
                            </b-badge>
                            <b-button class="nsp-power nsp-power-sm ml-2"
                                v-bind:variant="powerVariant(item.thermo && item.thermo.power)"
                                v-on:click.prevent="togglePanelThermostat(item)">
                                <i class="icon-power"></i>
                            </b-button>
                        </div>
                    </b-card-body>
                    <b-card-body v-if="item.thermo && item.thermo.power" class="pt-0 text-center"
                        v-on:mouseup="pushPanelTemp(item)" v-on:touchend="pushPanelTemp(item)">
                        <round-slider
                            v-model="item.thermo.setTemperature"
                            start-angle="0"
                            end-angle="+180"
                            line-cap="round"
                            min="10"
                            max="30"
                            step="0.5"
                            radius="80"/>
                        <div class="small text-muted">{{$t('NSP_HEATING')}}</div>
                    </b-card-body>
                </b-card>
                <b-card no-body class="modules" v-else-if="item.moduleVersion === 17">
                    <div class="line" v-bind:style="'background-color: '+getColor(item)+';'"></div>
                    <b-card-body class="pb-1 pt-1 pr-1">
                        <div v-if="hasTemperature(item)" class="float-left temperature">{{item.temperature}} &#8451;</div>
                        <div v-if="item.offline" class="mr-2 mt-1 text-right">
                            <i class="icon-exclamation" style="color: red;"></i>
                        </div>
                    </b-card-body>
                    <b-card-body class="pb-1 pt-1">
                        <div class="d-flex align-items-center">
                            <div class="module-icon module-icon-sm mr-2 module-drag">
                                <module-icon :module="item"/>
                            </div>
                            <h2 class="mb-0 mr-auto">{{item.moduleName}}</h2>
                            <b-button class="nsp-power nsp-power-sm ml-2"
                                v-bind:variant="powerVariant(isRelayOn(item))"
                                v-on:click.prevent="toggleDimmer(item)">
                                <i class="icon-power"></i>
                            </b-button>
                        </div>
                        <!-- brightness is a percentage, not a temperature, so it
                             gets the straight slider the recuperator fans use.
                             The dial is kept for temperatures alone. -->
                        <div class="px-3 pb-2" v-if="item.currentValue === 1">
                            <div class="d-flex align-items-center small text-muted">
                                <span>{{$t('BRIGHTNESS')}}</span>
                                <span class="ml-auto">{{item.values.brightness}}%</span>
                            </div>
                            <b-form-input
                                type="range"
                                v-model.number="item.values.brightness"
                                min="0"
                                max="100"
                                step="1"
                                v-on:change="setDimmer(item)"/>
                        </div>
                        <div class="row" v-if="item.currentValue === 1">
                            <div class="col-4" v-on:mouseup="setDimmer(item)">
                                <colorpicker v-model="item.values.ledcolor"></colorpicker>
                            </div>
                            <div class="col-8">
                                <select class="form-control form-control-sm" v-model="item.rgbEffect"
                                    v-on:change="setDimmer(item)">
                                    <option :value="0">{{ $t('RGBEFFECTNONE') }}</option>
                                    <option :value="1">{{ $t('RGBEFFECTMETEOR') }}</option>
                                    <option :value="2">{{ $t('RGBEFFECTGRADUAL') }}</option>
                                    <option :value="3">{{ $t('RGBEFFECTFLASH') }}</option>
                                </select>
                            </div>
                        </div>
                    </b-card-body>
                    <div v-if="item.tempShown">
                        <div class="m-3 text-right" v-on:click="setGlobalGraphData(item, 'temperature')">
                            <i class="icon-crop"></i>
                        </div>
                        <series-chart :points="temperaturePoints(item)" :label="$t('TEMPERATURE')" class="px-3" :height="140" />
                    </div>
                </b-card>
                <b-card no-body class="modules" v-else>
                    <div class="line" v-bind:style="'background-color: '+getColor(item)+';'"></div>
                    <b-card-body class="pb-1 pt-1 pr-1">
                        <div v-if="hasTemperature(item)" class="float-left temperature">{{item.temperature}} &#8451;</div>
                        <div v-if="item.analogDevice != 0 && item.analogDevice != null" class="float-left temperature ml-3">{{item.analogValue}} {{getAnalogMeasure(item)}}</div>
                        <div v-if="item.offline" class="mr-2 mt-1 text-right">
                            <i class="icon-exclamation" style="color: red;"></i>
                        </div>
                        <b-dropdown v-else class="float-right" variant="transparent p-0" right>
                            <template slot="button-content">
                                <i class="icon-settings"></i>
                            </template>
                            <b-dropdown-item v-if="hasTemperature(item)" v-on:click="toggleTemp(item)">{{$t('TOGGLETEMPERATUREVIEW')}}</b-dropdown-item>
                            <b-dropdown-item v-on:click="getTemp(item)">{{$t('REFRESHTEMPERATURE')}}</b-dropdown-item>
                            <b-dropdown-item v-on:click="reset(item)">{{$t('RESTART')}}</b-dropdown-item>
                            <b-dropdown-item v-if="item.analogDevice != 0 && item.analogDevice != null" v-on:click="toggleAnalog(item)">{{getAnalogLabel(item)}}</b-dropdown-item>
                            <b-dropdown-item v-if="item.irCodesData && item.irCodesData.length > 0" v-on:click="item.irCodesShown = !item.irCodesShown">{{$t('IRCODES')}}</b-dropdown-item>
                        </b-dropdown>
                    </b-card-body>
                    <b-card-body class="pb-1 pt-1">
                        <div class="d-flex align-items-center">
                            <div class="module-icon module-icon-sm mr-2 module-drag">
                                <module-icon :module="item"/>
                            </div>
                            <h2 class="mb-0 mr-auto">{{item.moduleName}}</h2>
                            <i v-if="isAlarm(item)"
                                class="mdi mdi-alert-circle module-alarm ml-2"
                                v-bind:class="isAlarmOn(item) ? 'text-danger' : 'text-muted'"
                                v-bind:title="isAlarmOn(item) ? $t('ALARM_TRIGGERED') : $t('ALARM_CLEAR')"></i>
            <template v-else-if="isPulseButton(item)">
                                <b-button class="nsp-power nsp-power-sm ml-2"
                                    variant="outline-secondary"
                                    v-bind:title="$t('PULSEBUTTON')"
                                    v-on:click.prevent="activateModule(item)">
                                    <i class="mdi mdi-gesture-tap"></i>
                                </b-button>
                            </template>
                            <template v-else>
                                <b-button class="nsp-power nsp-power-sm ml-2"
                                    v-bind:variant="powerVariant(isRelayOn(item))"
                                    v-on:click.prevent="activateModule(item)">
                                    <i v-bind:class="primaryIcon(item)"></i>
                                </b-button>
                                <b-button v-if="isShutter(item)" class="nsp-power nsp-power-sm ml-2"
                                    variant="outline-secondary"
                                    v-on:click.prevent="stopModule(item)">
                                    <i class="icon-control-pause"></i>
                                </b-button>
                                <b-button v-if="hasSecRelay(item)" class="nsp-power nsp-power-sm ml-2"
                                    v-bind:variant="powerVariant(isSecRelayOn(item))"
                                    v-on:click.prevent="activateSecModule(item)">
                                    <i v-bind:class="secondaryIcon(item)"></i>
                                </b-button>
                            </template>
                        </div>
                        <sensor-readings v-if="isSensorModule(item)" :module="item"/>
                    </b-card-body>
                    <div v-if="item.tempShown">
                        <div class="m-3 text-right" v-on:click="setGlobalGraphData(item, 'temperature')">
                            <i class="icon-crop"></i>
                        </div>
                        <series-chart :points="temperaturePoints(item)" :label="$t('TEMPERATURE')" class="px-3" :height="140" />
                    </div>
                    <div v-if="item.irCodesShown">
                        <ul class="list-group">
                            <li v-for="irCode in item.irCodesData" :key="irCode.irCodeId" class="list-group-item" v-on:click.prevent="sendCode(irCode, item.moduleId)">
                                <h6>{{irCode.deviceName}}</h6>
                                <h4>{{irCode.codeName}}</h4>
                            </li>
                        </ul>
                    </div>
                    <div v-if="item.analogShown">
                        <div class="m-3 text-right" v-on:click="setGlobalGraphData(item, 'analogValue')">
                            <i class="icon-crop"></i>
                        </div>
                        <series-chart :points="analogPoints(item)" :label="getAnalogLabel(item)" class="px-3" :height="140" />
                    </div>
                </b-card>
                </div>
            </draggable>
        </div>
        <div class="animated fadeIn row">
            <div class="col-sm-12" v-if="globalGraphShown">
                <b-card no-body class="modules">
                    <div class="m-3 text-right" v-on:click="globalGraphShown = false">
                        <i class="icon-close"></i>
                    </div>
                    <series-chart :points="globalGraphData.data" :label="globalGraphData.label" class="px-3" :height="240" />
                </b-card>
            </div>
        </div>
    </div>
</template>

<script>
import
{
    mapActions,
    mapGetters,
}
from 'vuex';
import Api from '@/api/Modules';
import IrApi from '@/api/IrCodes';
import SeriesChart from '@/components/lib/SeriesChart.vue';
import SensorReadings from '@/components/lib/SensorReadings.vue';
import WeatherCard from '@/components/lib/WeatherCard.vue';
import ModuleIcon from '@/components/lib/ModuleIcon.vue';
import BuildingsApi from '@/api/Buildings';
import ModuleCategoriesApi from '@/api/ModuleCategories';
import NsPanelApi from '@/api/NsPanel';
import { looseMatch } from '@/lib/search';

import { Chrome } from 'vue-color';
import draggable from 'vuedraggable';

// Only the weather station gets a card of its own above the grid. An indoor
// sensor sits in the grid like any other module, with its readings named
// underneath, which is how the app shows it too.
const SENSOR_VERSIONS = [20];

// kept in step with Modules.COVER_VERSIONS on the server
const COVER_VERSIONS = [15, 21, 24];

export default
{
    name: 'Dashboard',
    components:
    {
        'series-chart': SeriesChart,
        'weather-card': WeatherCard,
        'sensor-readings': SensorReadings,
        'module-icon': ModuleIcon,
        draggable,
        colorpicker: Chrome,
    },
    data()
    {
        return {
            data:
            {},
            // client side only, so typing does not go back to the server for a
            // list the page already holds
            filter: '',
            buildings: [],
            moduleCategories: [],
            foldedGroups: [],
            foldsKnown: false,
            activeBuildingTab: 0,
            globalGraphData:
            {},
            globalGraphShown: false,
            temp: false,
            pagination:
            {
                query: '',
                currentPage: 1,
                limit: 200,
                searchTimeout: false,
            },
        };
    },
    created()
    {
        const me = this;
        me.getData();
        me.getBuildings();
        me.getModuleCategories();

        // which cards were left closed, per browser
        try
        {
            const saved = JSON.parse(window.localStorage.getItem('dashboardFolded'));

            if (Array.isArray(saved))
            {
                me.foldedGroups = saved;
                me.foldsKnown = true;
            }
        }
        catch (error)
        {
            // a browser with no storage, or a value someone edited by hand. The
            // default below applies and nothing needs saying about it.
        }

        // An air conditioner is driven over infrared, so its state is not on the
        // module row like a relay's is - it arrives with the rest of the hvac.
        me.onHvac = (message) =>
        {
            me.$store.commit('NSPANEL_HVAC', message);
        };

        me.socket.on('nspanel', me.onHvac);

        me.socket.on('module', (message) =>
        {
            if (me.data.root)
            {
                for (const module of me.data.root)
                {
                    if (module.moduleId === message.moduleId)
                    {
                        me.$set(me.data.root, me.data.root.indexOf(module), message);
                    }
                }
            }
        });
    },
    methods:
    {
        sendCode(item, moduleId)
        {
            const me = this;
            item.moduleId = moduleId;
            IrApi.sendCode(item, (val) =>
            {
                if (val.error)
                {
                    me.$notify.notice(val);
                    me.formError = val;
                }
                else
                {
                    me.$notify.notice(val);
                    me.formError = {};
                    me.getData();
                }
            });
        },
        setGlobalGraphData(item, field)
        {
            const me = this;
            const analog = (field === 'analogValue');
            const label = analog ? me.getAnalogLabel(item) : me.$t('TEMPERATURE');
            const data = analog ? me.analogPoints(item) : me.temperaturePoints(item);

            me.globalGraphShown = false;
            me.$nextTick(() =>
            {
                me.globalGraphData = { data, field, label };
                me.globalGraphShown = true;
            });
        },
        activateModule(item)
        {
            const me = this;
            Api.activateModule(item, (data) =>
            {
                if (data.error && data.error == true)
                {
                    me.$notify.notice(data);
                }
            });
        },
        setThermostat(item)
        {
            const me = this;
            Api.setThermostat(item).catch((error) =>
            {
                me.$notify.notice(error);
            });
        },
        setDimmer(item)
        {
            const me = this;
            item.brightness = item.values.brightness;
            // the strip runs the effect itself; setDimmer stores it so a rule
            // switching this light on later does not cancel it
            item.effect = (item.rgbEffect == null) ? 0 : item.rgbEffect;
            if (item.values && item.values.ledcolor && item.values.ledcolor.rgba)
            {
                item.ledcolor = {
                    r: item.values.ledcolor.rgba.r,
                    g: item.values.ledcolor.rgba.g,
                    b: item.values.ledcolor.rgba.b,
                };
            }
            Api.setDimmer(item).catch((error) =>
            {
                me.$notify.notice(error);
            });
        },
        setAcPower(on)
        {
            const me = this;

            NsPanelApi.setAcPower({ on }, (data) =>
            {
                if (data && data.error)
                {
                    me.$notify.notice(data);
                }
            });
        },
        setAcMode(mode)
        {
            const me = this;

            NsPanelApi.setAcMode({ mode }, (data) =>
            {
                if (data && data.error)
                {
                    me.$notify.notice(data);
                }
            });
        },
        activateSecModule(item)
        {
            const me = this;
            Api.activateSecModule(item, (data) =>
            {
                if (data.error && data.error == true)
                {
                    me.$notify.notice(data);
                }
            });
        },
        stopModule(item)
        {
            const me = this;

            Api.stopModule(item, (data) =>
            {
                if (data && data.error)
                {
                    me.$notify.notice(data);
                }
            });
        },
        getTemp(item)
        {
            const me = this;
            Api.getTemp(item, (data) =>
            {
                if (data.error && data.error == true)
                {
                    me.$notify.notice(data);
                }
            });
        },
        reset(item)
        {
            const me = this;
            Api.reset(item, (data) =>
            {
                if (data.error && data.error == true)
                {
                    me.$notify.notice(data);
                }
            });
        },
        getColor(item)
        {
            const me = this;
            if (item.currentValue == 1)
            {
                return item.iconColor;
            }
            return '#ccc';
        },
        getSecColor(item)
        {
            const me = this;
            if (item.currentSecValue == 1)
            {
                return item.iconColor;
            }
            return '#ccc';
        },
        /**
         * The device icon now only identifies the device. On and off is the power
         * button beside it, so the icon keeps its own colour instead of greying
         * out - it was carrying two meanings at once before.
         */
        /**
         * An nspanel keeps its thermostat in moduleData, as text. The defaults
         * are the ones NSPanel.getState uses, so the dashboard and the house
         * screen show the same thing for a panel that has never been set.
         */
        panelThermostat(item)
        {
            let data = {};

            try
            {
                data = JSON.parse(item.moduleData) || {};
            }
            catch (error)
            {
                data = {};
            }

            return {
                power: (data.power === true),
                setTemperature: (data.setTemperature === undefined) ? 23 : data.setTemperature,
            };
        },
        togglePanelThermostat(item)
        {
            const me = this;
            const power = !(item.thermo && item.thermo.power);

            me.$set(item.thermo, 'power', power);

            Api.setRoomThermostat({ moduleId: item.moduleId, power }, (data) =>
            {
                if (data && data.error)
                {
                    me.$notify.notice(data);
                }
            });
        },
        // on release rather than on drag, so one turn of the dial is one write
        pushPanelTemp(item)
        {
            const me = this;

            Api.setRoomThermostat(
                { moduleId: item.moduleId, setTemperature: item.thermo.setTemperature },
                (data) =>
                {
                    if (data && data.error)
                    {
                        me.$notify.notice(data);
                    }
                },
            );
        },
        hasTemperature(item)
        {
            return (item.temperature != null);
        },
        /**
         * History is a separate call now. It used to be read inside selectRow, one
         * query per row of the list, which is why it was commented out and every
         * graph has been empty since. Fetched when a graph is opened instead.
         */
        loadHistory(item)
        {
            const me = this;

            if (item.logLoading)
            {
                return;
            }

            me.$set(item, 'logLoading', true);

            Api.getModuleHistory({ moduleId: item.moduleId }, (data) =>
            {
                me.$set(item, 'logLoading', false);

                if (!data || data.error)
                {
                    return;
                }

                me.$set(item, 'logData', data.log || []);
            });
        },
        toggleTemp(item)
        {
            const me = this;

            item.tempShown = !item.tempShown;

            if (item.tempShown)
            {
                me.loadHistory(item);
            }
        },
        toggleAnalog(item)
        {
            const me = this;

            item.analogShown = !item.analogShown;

            if (item.analogShown)
            {
                me.loadHistory(item);
            }
        },
        temperaturePoints(item)
        {
            return (item.logData || [])
                .filter((row) => row.temperature != null)
                .map((row) => ({ dateTime: row.dateTime, value: row.temperature }));
        },
        analogPoints(item)
        {
            return (item.logData || [])
                .filter((row) => row.analogValue != null)
                .map((row) => ({ dateTime: row.dateTime, value: row.analogValue }));
        },
        // the sensor cards pick their modules out of the list already loaded, all
        // of them - a second station used to hide the first
        modulesOfVersion(moduleVersion)
        {
            const me = this;

            if (!me.data.root)
            {
                return [];
            }

            return me.data.root.filter((item) => item.moduleVersion === moduleVersion
                && me.inBuilding(item)
                && me.matchesFilter(item));
        },
        isFolded(key)
        {
            return this.foldedGroups.indexOf(key) !== -1;
        },
        toggleGroup(key)
        {
            const me = this;
            const at = me.foldedGroups.indexOf(key);

            if (at === -1)
            {
                me.foldedGroups.push(key);
            }
            else
            {
                me.foldedGroups.splice(at, 1);
            }

            // a choice by hand is an answer, so the default below stops applying
            me.foldsKnown = true;

            // remembered per browser, so a card left closed stays closed
            window.localStorage.setItem('dashboardFolded', JSON.stringify(me.foldedGroups));
        },
        /**
         * Renumbers one category after a card was dragged inside it.
         *
         * Only the cards in that group are touched. Numbers are spaced by ten so
         * one can later be dropped between two without renumbering the rest, and
         * they are set on the objects the grid already holds so the list
         * re-sorts itself immediately.
         */
        reorderGroup(group, value)
        {
            const me = this;

            value.forEach((item, index) =>
            {
                item.orderNum = (index + 1) * 10;
            });

            Api.saveOrder({ order: value.map((one) => one.moduleId) }, (data) =>
            {
                if (data && data.error)
                {
                    me.$notify.notice(data);
                }
            });
        },
        // anything that reports numbers rather than switching. Its readings are
        // drawn under the name, named, the way the app draws them
        isSensorModule(item)
        {
            return [20, 22].indexOf(item.moduleVersion) !== -1;
        },
        // name or mac, loosely - see src/lib/search.js
        matchesFilter(item)
        {
            return looseMatch(this.filter, [item.moduleName, item.moduleMac]);
        },
        inBuilding(item)
        {
            const me = this;
            const own = (item.buildingId == null) ? null : parseInt(item.buildingId, 10);

            return (own === me.activeBuildingId);
        },
        async getModuleCategories()
        {
            const me = this;

            try
            {
                const data = await ModuleCategoriesApi.selectRow({ start: 0, limit: 200 });

                if (data && !data.error)
                {
                    me.moduleCategories = data.root || [];
                }
            }
            catch (error)
            {
                // the grid still works, everything just lands under uncategorised
            }
        },
        async getBuildings()
        {
            const me = this;

            try
            {
                const data = await BuildingsApi.selectRow({ start: 0, limit: 200 });

                if (data && !data.error)
                {
                    me.buildings = data.root || [];
                }
            }
            catch (error)
            {
                me.buildings = [];
            }
        },
        powerVariant(on)
        {
            return on ? 'success' : 'outline-secondary';
        },
        toggleThermostat(item)
        {
            const me = this;

            item.values.deviceOn = !me.isThermostatOn(item);
            me.setThermostat(item);
        },
        toggleDimmer(item)
        {
            const me = this;

            item.power = !me.isRelayOn(item);
            me.setDimmer(item);
        },
        hasSecRelay(item)
        {
            return (item.digitalDevice === 2 || item.digitalDevice === 3
                || item.moduleVersion === 10 || this.isCover(item));
        },
        /**
         * Sensors with nothing to switch: flood and other switch sensors, motion,
         * door and window contacts, smoke. A power button on these was never
         * meaningful - there is no relay behind it - so they show an alert mark
         * that turns red while the sensor reads triggered.
         */
        isAlarm(item)
        {
            return ([2, 7, 8, 12].indexOf(item.moduleVersion) !== -1);
        },
        // flood, motion and smoke all report through buttonStatus now, which
        // setValues copies into currentValue, so there is one field to read
        isAlarmOn(item)
        {
            return (Number(item.currentValue) === 1);
        },
        // shelly 2pm and the shutter type, both open and close a cover
        // One definition, because there were two: this one and isShutter, with
        // different lists. Adding a version to the constant updated only one of
        // them, so a remote driven awning got its up and its stop but no down.
        isCover(item)
        {
            return this.isShutter(item);
        },
        // a shutter opens and closes rather than switching on and off, and unlike
        // a relay it can be caught halfway, so it gets a stop between the arrows
        // shelly 2pm and the shutter type: two channels that are two directions,
        // not an on and an off. The server and the app both call these covers;
        // only this screen was treating a version 15 as an ordinary two relay
        // module and offering two power buttons for it.
        // a relay wired to a remote's button. It is a press, not a position, so
        // it never draws as on - nothing can be read back to say that it is
        isPulseButton(item)
        {
            return (item.moduleVersion === 25);
        },
        isShutter(item)
        {
            return COVER_VERSIONS.indexOf(item.moduleVersion) !== -1;
        },
        primaryIcon(item)
        {
            return this.isShutter(item) ? 'icon-arrow-up' : 'icon-power';
        },
        secondaryIcon(item)
        {
            return this.isShutter(item) ? 'icon-arrow-down' : 'icon-power';
        },
        // currentValue arrives as a string on some module types
        isRelayOn(item)
        {
            return (Number(item.currentValue) === 1);
        },
        isSecRelayOn(item)
        {
            return (Number(item.currentSecValue) === 1);
        },
        isThermostatOn(item)
        {
            return (item.values.deviceOn === 'true' || item.values.deviceOn === true);
        },
        getAnalogLabel(item)
        {
            const me = this;
            switch (item.analogDevice)
            {
            case 1:
                return me.$t('SOUNDDBLEVEL');
                break;
            case 2:
                return me.$t('PRESSURELEVEL');
                break;
            }
        },
        getAnalogMeasure(item)
        {
            const me = this;
            switch (item.analogDevice)
            {
            case 1:
                return me.$t('DB');
                break;
            case 2:
                return me.$t('BAR');
                break;
            case 3:
                return '%';
                break;
            }
        },
        getData()
        {
            const me = this;
            Api.selectRow(me.getParams, function (data)
            {
                if (data.error && data.error == true)
                {
                    me.$notify.notice(data);
                    return;
                }

                for (const item of data.root)
                {
                    let found = false;
                    if (me.data.root)
                    {
                        for (const oldItem of me.data.root)
                        {
                            if (oldItem.moduleId == item.moduleId)
                            {
                                found = true;
                                item.analogShown = oldItem.analogShown;
                                item.tempShown = oldItem.tempShown;
                                item.irCodesShown = oldItem.irCodesShown;
                                break;
                            }
                        }
                    }

                    if (!found)
                    {
                        item.analogShown = false;
                        item.tempShown = false;
                        item.irCodesShown = false;
                    }

                    if (item.moduleVersion === 13)
                    {
                        item.thermo = me.panelThermostat(item);
                    }
                }

                me.data = data;
            });
        },
    },
    watch:
    {
        /**
         * With nothing chosen yet, open the first category and close the rest,
         * so the page starts on one card rather than on all of them.
         *
         * Fires once and then hands over: the first fold done by hand sets
         * foldsKnown and this stops applying.
         */
        moduleGroups:
        {
            immediate: true,
            handler(groups)
            {
                const me = this;

                if (me.foldsKnown || groups.length === 0)
                {
                    return;
                }

                me.foldsKnown = true;
                me.foldedGroups = groups.slice(1).map((one) => one.key);
            },
        },
        'pagination.currentPage'(val)
        {
            const me = this;
            me.getData();
        },
        'pagination.query'(val)
        {
            const me = this;

            if (me.pagination.searchTimeout != false)
            {
                clearTimeout(me.pagination.searchTimeout);
            }

            me.pagination.searchTimeout = setTimeout(function ()
            {
                me.getData();
                me.pagination.searchTimeout = false;
            }, 300);
        },
    },
    computed:
    {
        ...mapGetters([
            'globals',
            'socket',
            'nspanel',
        ]),
        // the hvac arrives over the socket and may not have yet
        acState()
        {
            const me = this;

            return (me.nspanel && me.nspanel.ac) ? me.nspanel.ac : {};
        },
        acTempState()
        {
            const me = this;

            return (me.nspanel && me.nspanel.acTemp) ? me.nspanel.acTemp : {};
        },
        /**
         * The buildings this company has, with unclaimed always last. Unclaimed
         * is not a leftover - a module without a building is perfectly usable and
         * has to be reachable, so it gets a tab of its own rather than vanishing.
         */
        buildingTabs()
        {
            const me = this;

            return [...me.buildings, { buildingId: null, name: me.$t('UNCLAIMED') }];
        },
        activeBuildingId()
        {
            const tab = this.buildingTabs[this.activeBuildingTab];

            return (tab == null) ? null : tab.buildingId;
        },
        weatherStations()
        {
            return this.modulesOfVersion(20);
        },
        gridModules()
        {
            const me = this;

            if (!me.data.root)
            {
                return [];
            }

            // The weather station has its own row above, keep it out of the grid.
            //
            // Sorted here rather than relying on the order the server sent, so
            // dragging a card takes effect the moment orderNum changes instead
            // of after a refetch.
            return me.data.root
                .filter((item) => SENSOR_VERSIONS.indexOf(item.moduleVersion) === -1
                    && me.inBuilding(item)
                    && me.matchesFilter(item))
                .sort((a, b) =>
                {
                    const left = parseInt(a.orderNum, 10) || 0;
                    const right = parseInt(b.orderNum, 10) || 0;

                    if (left !== right)
                    {
                        return left - right;
                    }

                    return String(a.moduleName).localeCompare(String(b.moduleName));
                });
        },
        /**
         * The grid split into one card per category, uncategorised last.
         *
         * A category a module can carry but that has nothing in it under this
         * building tab is left out, so the screen shows what is there rather
         * than a row of empty headings.
         */
        moduleGroups()
        {
            const me = this;
            const byCategory = {};

            for (const item of me.gridModules)
            {
                const key = (item.moduleCategoryId == null) ? 'none' : String(item.moduleCategoryId);

                if (byCategory[key] == null)
                {
                    byCategory[key] = [];
                }

                byCategory[key].push(item);
            }

            const groups = me.moduleCategories
                .filter((one) => byCategory[String(one.moduleCategoryId)] != null)
                .map((one) => ({
                    key: String(one.moduleCategoryId),
                    name: one.name,
                    modules: byCategory[String(one.moduleCategoryId)],
                }));

            if (byCategory.none != null)
            {
                groups.push({ key: 'none', name: me.$t('UNCATEGORISED'), modules: byCategory.none });
            }

            return groups;
        },
        getParams()
        {
            const me = this;
            const data = {
                ...me.pagination,
            };
            data.start = (data.currentPage - 1) * data.limit;
            return data;
        },
    },
};
</script>
